/** @jsxImportSource @emotion/react */
import React from 'react'
import { TextInput } from '../TextInput'

/**
 * NumberInput component for numeric form fields with min/max constraints
 * Automatically enforces min/max values by clamping input to the specified range
 * @component
 */
type NumberInputProps = {
  /** Field name for error handling and identification */
  name: string
  /** Current input value */
  value: string
  /** Function to update the input value */
  setValue: (value: string) => void
  /** Object containing field validation errors */
  fieldErrors: any
  /** Custom error message */
  customError: any
  /** Label text displayed above the input */
  label: string
  /** Whether the input is disabled */
  disabled?: boolean
  /** Minimum allowed value */
  min?: number
  /** Maximum allowed value */
  max?: number
  /** Placeholder text */
  placeholder?: string
  /** Tab index for keyboard navigation */
  tabIndex?: number
}

export const NumberInput: React.FC<NumberInputProps> = ({
  name,
  value,
  setValue,
  fieldErrors,
  customError,
  label,
  disabled = false,
  min,
  max,
  placeholder,
  tabIndex,
}) => {
  // Handle value change with number validation and min/max enforcement
  const handleChange = (newValue: string) => {
    // Allow empty string for clearing the field
    if (newValue === '') {
      setValue('')
      return
    }

    // Only allow numeric input (including negative numbers and decimals)
    const numericRegex = /^-?\d*\.?\d*$/
    if (!numericRegex.test(newValue)) {
      return // Don't update if not a valid number format
    }

    // Convert to number for range validation
    const numericValue = parseFloat(newValue)

    // Skip validation if the number is incomplete (ends with . or -)
    if (isNaN(numericValue) || newValue.endsWith('.') || newValue === '-') {
      setValue(newValue)
      return
    }

    // Enforce min/max constraints by clamping the value
    let clampedValue = numericValue
    if (min !== undefined && numericValue < min) {
      clampedValue = min
    } else if (max !== undefined && numericValue > max) {
      clampedValue = max
    }

    // Convert back to string, preserving the original if no clamping occurred
    const finalValue =
      clampedValue === numericValue ? newValue : clampedValue.toString()
    setValue(finalValue)
  }

  // Create enhanced label with min/max info
  const enhancedLabel = React.useMemo(() => {
    if (min !== undefined && max !== undefined) {
      return `${label} (${min}-${max})`
    } else if (min !== undefined) {
      return `${label} (min: ${min})`
    } else if (max !== undefined) {
      return `${label} (max: ${max})`
    }
    return label
  }, [label, min, max])

  return (
    <TextInput
      name={name}
      value={value}
      setValue={handleChange}
      fieldErrors={fieldErrors}
      customError={customError}
      label={enhancedLabel}
      type='number'
      disabled={disabled}
      min={min}
      max={max}
      placeholder={placeholder || `Enter ${label.toLowerCase()}`}
      tabIndex={tabIndex}
    />
  )
}
